import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save, Send, Check, Copy, FileText, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, fmtUSD2, toDateInput, fromDateInput } from "@/lib/api";
import { PRODUCT_TYPES, productTypeLabel, quoteStatusLabel } from "@/lib/constants";
import { Pill } from "@/components/app/shared";
import SignaturePad from "@/components/crm/SignaturePad";

const QUOTE_TONE = {
  DRAFT: "neutral", SENT: "sky", VIEWED: "butter", ACCEPTED: "mint", DECLINED: "blush", EXPIRED: "neutral",
};

const emptyItem = () => ({
  _k: Math.random().toString(36).slice(2),
  productId: null, productType: "SPONSORED_WEB", description: "", qty: 1, unitPrice: 0, discount: 0,
});

function computeLocal(items, taxRate, extraDiscount) {
  let subtotal = 0;
  let lineDiscount = 0;
  items.forEach((it) => {
    const qty = Number(it.qty) || 0;
    const up = Number(it.unitPrice) || 0;
    const dsc = Math.max(Number(it.discount) || 0, 0);
    const line = qty * up;
    subtotal += line;
    lineDiscount += Math.min(dsc, line);
    it.lineTotal = Math.max(line - dsc, 0);
  });
  const extra = Math.max(Number(extraDiscount) || 0, 0);
  const base = Math.max(subtotal - lineDiscount - extra, 0);
  const tax = base * (Number(taxRate) || 0);
  const total = base + tax;
  return { subtotal, discountTotal: lineDiscount + extra, taxableBase: base, taxTotal: tax, total };
}

export default function QuoteBuilderDialog({ open, onOpenChange, deal, quoteId }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptorName, setAcceptorName] = useState("");
  const [sig, setSig] = useState("");

  const { data: productsData } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
    enabled: open,
  });
  const products = productsData?.products || [];

  const { data: quoteDetail, isLoading: loadingQuote } = useQuery({
    queryKey: ["quote", quoteId],
    queryFn: async () => (await api.get(`/quotes/${quoteId}`)).data,
    enabled: open && !!quoteId,
  });

  useEffect(() => {
    if (!open) return;
    if (quoteId) {
      if (!quoteDetail) return;
      const q = quoteDetail.quote;
      setForm({
        ...q,
        items: (q.items || []).map((it) => ({ ...it, _k: Math.random().toString(36).slice(2) })),
      });
    } else if (deal) {
      setForm({
        id: null,
        quoteNumber: "— (se asigna al guardar)",
        status: "DRAFT",
        clientName: deal.clientName || "",
        contactName: deal.contactName || "",
        contactEmail: deal.contactEmail || "",
        items: [emptyItem()],
        taxRate: 0.13,
        extraDiscount: 0,
        terms: "",
        schedule: "",
        notes: "",
        validUntil: null,
        publicToken: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteId, quoteDetail?.quote?.id, deal?.id]);

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, discountTotal: 0, taxableBase: 0, taxTotal: 0, total: 0 };
    return computeLocal(form.items || [], form.taxRate, form.extraDiscount);
  }, [form]);

  const inv = () => {
    qc.invalidateQueries({ queryKey: ["deals"] });
    qc.invalidateQueries({ queryKey: ["quotes"] });
    qc.invalidateQueries({ queryKey: ["crm-revenue"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["guide-first-deal"] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const items = (form.items || []).map(({ _k, lineTotal, ...rest }) => ({
        ...rest, qty: Number(rest.qty) || 0, unitPrice: Number(rest.unitPrice) || 0, discount: Number(rest.discount) || 0,
      }));
      const payload = {
        clientName: form.clientName, contactName: form.contactName, contactEmail: form.contactEmail,
        items, taxRate: Number(form.taxRate) || 0, extraDiscount: Number(form.extraDiscount) || 0,
        terms: form.terms, schedule: form.schedule, notes: form.notes, validUntil: form.validUntil,
      };
      if (form.id) return (await api.patch(`/quotes/${form.id}`, payload)).data;
      return (await api.post("/quotes", { dealId: deal.id, ...payload })).data;
    },
    onSuccess: (q) => { toast.success(form.id ? "Cotización actualizada" : `Cotización creada: ${q.quoteNumber}`); inv(); if (!form.id) setForm({ ...form, id: q.id, quoteNumber: q.quoteNumber, publicToken: q.publicToken }); qc.invalidateQueries({ queryKey: ["quote", form.id || q.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const markSent = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${form.id}/mark-sent`)).data,
    onSuccess: (q) => { toast.success("Marcada como enviada"); setForm({ ...form, ...q }); inv(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const generatePdf = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${form.id}/generate-pdf`)).data,
    onSuccess: (r) => { toast.info(r.message || "Generando PDF…"); qc.invalidateQueries({ queryKey: ["quotes", form.dealId || deal?.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const acceptInternal = useMutation({
    mutationFn: async () => (await api.post(`/quotes/${form.id}/accept`, { acceptorName, signature: sig })).data,
    onSuccess: (q) => { toast.success(`Cotización ${q.quoteNumber} aceptada`); setForm({ ...form, ...q }); setAcceptOpen(false); setAcceptorName(""); setSig(""); inv(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/quotes/${form.id}`),
    onSuccess: () => { toast.success("Cotización eliminada"); inv(); onOpenChange(false); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!open) return null;
  if (loadingQuote && quoteId) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl"><div className="flex h-80 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div></DialogContent>
      </Dialog>
    );
  }
  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const pickProduct = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    setItem(idx, { productId: p.id, productType: p.productType, description: p.description || p.name, unitPrice: p.defaultUnitPrice });
  };

  const locked = form.status === "ACCEPTED" || form.status === "DECLINED";
  const publicUrl = form.publicToken ? `${window.location.origin}/q/${form.publicToken}` : null;

  const copyPublic = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Enlace público copiado"); }
    catch { toast.error("No se pudo copiar"); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-card" data-testid="quote-builder">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="kicker">{form.id ? form.quoteNumber : "Nueva cotización"}</div>
                <DialogTitle className="font-display text-4xl font-normal">Cotización · {form.clientName || deal?.clientName}</DialogTitle>
                <DialogDescription>
                  {form.id ? `Estado: ${quoteStatusLabel(form.status)}` : "Agrega los items, aplica IVA y envía o acepta la cotización."}
                </DialogDescription>
              </div>
              {form.id && <Pill tone={QUOTE_TONE[form.status] || "neutral"} data-testid="quote-status-pill">{quoteStatusLabel(form.status)}</Pill>}
            </div>
          </DialogHeader>

          {/* client fields */}
          <div className="grid gap-3 md:grid-cols-3">
            <div className="grid gap-1.5"><Label className="text-xs">Cliente</Label><Input value={form.clientName} disabled={locked} onChange={(e) => set("clientName", e.target.value)} data-testid="quote-client-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Contacto</Label><Input value={form.contactName} disabled={locked} onChange={(e) => set("contactName", e.target.value)} data-testid="quote-contact-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs">Correo de contacto</Label><Input type="email" value={form.contactEmail} disabled={locked} onChange={(e) => set("contactEmail", e.target.value)} data-testid="quote-email-input" /></div>
          </div>

          <Separator />

          {/* items */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="kicker">Items</div>
              {!locked && <Button size="sm" variant="outline" onClick={addItem} data-testid="quote-add-item-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Agregar</Button>}
            </div>
            <div className="rounded-md border border-border bg-background">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Producto", "Descripción", "Cant.", "Precio USD", "Dscto. USD", "Subtotal", ""].map((h) => (
                      <TableHead key={h} className="text-[11px] uppercase tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.items.map((it, idx) => (
                    <TableRow key={it._k} data-testid="quote-item-row">
                      <TableCell className="w-[220px] align-top">
                        {products.length ? (
                          <Select value={it.productId || ""} onValueChange={(v) => pickProduct(idx, v)} disabled={locked}>
                            <SelectTrigger className="h-8 text-xs" data-testid="quote-product-select"><SelectValue placeholder="Elige producto" /></SelectTrigger>
                            <SelectContent>
                              {products.filter((p) => p.active).map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : <span className="text-xs text-muted-foreground">Sin catálogo</span>}
                        <div className="mt-1.5">
                          <Select value={it.productType} onValueChange={(v) => setItem(idx, { productType: v })} disabled={locked}>
                            <SelectTrigger className="h-7 text-[11px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{PRODUCT_TYPES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell className="align-top"><Textarea rows={2} value={it.description} disabled={locked} onChange={(e) => setItem(idx, { description: e.target.value })} className="min-h-[52px] text-xs" data-testid="quote-item-description" /></TableCell>
                      <TableCell className="w-[70px] align-top"><Input type="number" min="0" value={it.qty} disabled={locked} onChange={(e) => setItem(idx, { qty: e.target.value })} className="h-8 font-mono text-xs" data-testid="quote-item-qty" /></TableCell>
                      <TableCell className="w-[110px] align-top"><Input type="number" min="0" step="0.01" value={it.unitPrice} disabled={locked} onChange={(e) => setItem(idx, { unitPrice: e.target.value })} className="h-8 font-mono text-xs" data-testid="quote-item-price" /></TableCell>
                      <TableCell className="w-[110px] align-top"><Input type="number" min="0" step="0.01" value={it.discount} disabled={locked} onChange={(e) => setItem(idx, { discount: e.target.value })} className="h-8 font-mono text-xs" data-testid="quote-item-discount" /></TableCell>
                      <TableCell className="w-[110px] align-top font-mono text-xs">{fmtUSD2(it.lineTotal || 0)}</TableCell>
                      <TableCell className="w-[40px] align-top">
                        {!locked && form.items.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(idx)} aria-label="Quitar" data-testid="quote-item-remove"><X className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div className="md:col-span-2 grid gap-3">
              <div className="grid gap-1.5"><Label className="text-xs">Calendario / cronograma</Label><Input value={form.schedule} disabled={locked} onChange={(e) => set("schedule", e.target.value)} placeholder="Ej. Publicación en marzo 2026, campaña del 10 al 24" data-testid="quote-schedule-input" /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Términos y condiciones</Label><Textarea rows={5} value={form.terms} disabled={locked} onChange={(e) => set("terms", e.target.value)} className="font-mono text-xs" data-testid="quote-terms-input" /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Notas internas (no se muestran al cliente)</Label><Textarea rows={2} value={form.notes} disabled={locked} onChange={(e) => set("notes", e.target.value)} data-testid="quote-notes-input" /></div>
            </div>
            <div className="grid gap-3 rounded-md border border-border bg-surface-2 p-4" data-testid="quote-totals-panel">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Label className="text-xs">IVA (%)</Label>
                <Input type="number" min="0" max="1" step="0.01" value={form.taxRate} disabled={locked} onChange={(e) => set("taxRate", e.target.value)} className="h-8 font-mono text-xs" data-testid="quote-tax-input" />
                <Label className="text-xs">Dscto. global (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.extraDiscount || 0} disabled={locked} onChange={(e) => set("extraDiscount", e.target.value)} className="h-8 font-mono text-xs" data-testid="quote-extra-discount-input" />
                <Label className="text-xs">Válida hasta</Label>
                <Input type="date" value={toDateInput(form.validUntil)} disabled={locked} onChange={(e) => set("validUntil", fromDateInput(e.target.value))} className="h-8 text-xs" data-testid="quote-valid-until-input" />
              </div>
              <Separator />
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{fmtUSD2(totals.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Descuentos</span><span className="font-mono">−{fmtUSD2(totals.discountTotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Base gravable</span><span className="font-mono">{fmtUSD2(totals.taxableBase)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="font-mono">{fmtUSD2(totals.taxTotal)}</span></div>
              </div>
              <Separator />
              <div className="flex items-end justify-between">
                <span className="kicker">Total</span>
                <span className="font-display text-3xl leading-none" data-testid="quote-total-value">{fmtUSD2(totals.total)}</span>
              </div>
            </div>
          </div>

          {form.id && publicUrl && (
            <div className="flex flex-col gap-2 rounded-md border border-dashed border-border bg-surface-2 p-3 text-xs md:flex-row md:items-center md:justify-between" data-testid="quote-public-link">
              <div className="min-w-0">
                <div className="kicker">Enlace público de aceptación</div>
                <div className="mt-0.5 truncate font-mono text-[11px] text-foreground">{publicUrl}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={copyPublic} data-testid="quote-copy-link-button"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar</Button>
                <Button size="sm" variant="outline" asChild><a href={publicUrl} target="_blank" rel="noreferrer">Abrir</a></Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <div className="flex gap-2">
              {form.id && !locked && (
                <Button variant="outline" size="icon" onClick={() => del.mutate()} disabled={del.isPending} aria-label="Eliminar" data-testid="quote-delete-button"><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {!locked && <Button variant="outline" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} data-testid="quote-save-button">{saveMutation.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Guardar</Button>}
              {form.id && (
                <Button variant="outline" onClick={() => generatePdf.mutate()} disabled={generatePdf.isPending} data-testid="quote-pdf-button"><FileText className="mr-1.5 h-4 w-4" /> Generar PDF</Button>
              )}
              {form.id && !locked && form.status === "DRAFT" && (
                <Button variant="outline" onClick={() => markSent.mutate()} disabled={markSent.isPending} data-testid="quote-mark-sent-button"><Send className="mr-1.5 h-4 w-4" /> Marcar como enviada</Button>
              )}
              {form.id && !locked && (
                <Button onClick={() => setAcceptOpen(true)} data-testid="quote-accept-button"><Check className="mr-1.5 h-4 w-4" /> Marcar como aceptada</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <AlertDialogContent data-testid="quote-accept-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-3xl">Aceptación interna</AlertDialogTitle>
            <AlertDialogDescription>
              Captura el nombre y firma de quien aceptó por correo o teléfono. Esto moverá el deal a estado “Ganado / Reservado”.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1.5">
              <Label className="text-xs">Nombre completo del aceptante</Label>
              <Input value={acceptorName} onChange={(e) => setAcceptorName(e.target.value)} data-testid="quote-accept-name-input" />
            </div>
            <SignaturePad value={sig} onChange={setSig} label="Firma del cliente" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quote-accept-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); if (acceptorName.trim() && sig) acceptInternal.mutate(); else toast.error("Nombre y firma requeridos"); }}
              disabled={acceptInternal.isPending}
              data-testid="quote-accept-confirm"
            >
              {acceptInternal.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Confirmar aceptación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
